import React, { useEffect, useId, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuUserPlus } from 'react-icons/lu';
import { db } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';
import { EMPLOYEE_ROLE_OPTIONS } from '../../constants/employeeGroups';
import { PROFESSION_OPTIONS, formatLanguageInfo } from '../../constants/employeeOptions';
import GlassIcon from '../../components/ui/GlassIcon';
import SegmentedControl from '../../components/ui/SegmentedControl';
// Only the global variables of this stylesheet are still in use (by other
// legacy dialogs); importing it keeps its place in the cascade.
import './AddEmployeeModal.module.css';
import { EMPLOYEE_COLUMNS, optionLabel } from './employeeColumns';
import EmployeeFormShell from './employeeForm/EmployeeFormShell';
import { CompanyEmailField, Field, SelectInput, TextArea, TextInput, ToggleTile } from './employeeForm/FormControls';
import LanguageEditor from './employeeForm/LanguageEditor';
import useEmployeeFormOptions from './employeeForm/useEmployeeFormOptions';
import { getFormSections, summarizeErrors } from './employeeForm/formSections';
import {
  NATIONALITY_OPTIONS,
  PHONE_PATTERN,
  formatPhoneInput,
  isValidLocalPart,
  localPartOf,
} from './employeeForm/employeeFormOptions';

const INITIAL_FORM = {
  eName: '',
  eSurname: '',
  Active: 1,
  address: '', // Ev adresi (alan adı geriye dönük uyumluluk için 'address')
  workAddress: '',
  annualPermit: 14,
  birthday: '',
  departman: '',
  eMail: '',
  isHandicappedPerson: 'No',
  isIntern: 'No',
  isRetired: 'No',
  languageInfo: '',
  nationality: 'Türk',
  phone: '',
  emergencyPhone: '', // Acil durum telefonu
  profession: '',
  educationInfo: '',
  startDate: '',
  tcNumber: '',
  workType: '',
  authorization: '',
  role: '',
};

const REQUIRED_FIELDS = ['eName', 'eSurname', 'tcNumber'];
const FLAG_FIELDS = ['isHandicappedPerson', 'isRetired', 'isIntern'];
const PHONE_FIELDS = ['phone', 'emergencyPhone'];

const asOptions = (values) => values.map((value) => ({ value, label: value }));

/** "Yeni Çalışan Ekle": the new employee form, in sections. */
const AddEmployeeModal = ({ onClose, onUpdate }) => {
  const { t } = useTranslation();
  const idPrefix = useId();
  const fieldId = (name) => `${idPrefix}-${name}`;
  const { departments, authorizationRoles, isLoadingDepartments, isLoadingAuthorizationRoles } = useEmployeeFormOptions();
  const [formData, setFormData] = useState(INITIAL_FORM);
  // Dil bilgisi editörünün çalışma listesi; kayıt formatına (string) çevrimi
  // aşağıdaki effect formData.languageInfo üzerinden yapar
  const [languageEntries, setLanguageEntries] = useState([]);
  const [activeSection, setActiveSection] = useState('personal');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, languageInfo: formatLanguageInfo(languageEntries) }));
  }, [languageEntries]);

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setDirty(true);
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    // Convert name and surname to uppercase with Turkish locale support
    setField(name, name === 'eName' || name === 'eSurname' ? value.toLocaleUpperCase('tr-TR') : value);
  };

  // Phone fields keep digits only, shown as 5XX XXX XX XX
  const phoneHandlers = (name) => ({
    onChange: (event) => setField(name, formatPhoneInput(event.target.value)),
    onPaste: (event) => {
      event.preventDefault();
      setField(name, formatPhoneInput((event.clipboardData || window.clipboardData).getData('text')));
    },
  });

  const validate = () => {
    const found = {};
    REQUIRED_FIELDS.forEach((name) => {
      if (!String(formData[name] || '').trim()) found[name] = t('employees.form.required');
    });
    const local = localPartOf(formData.eMail);
    if (local && !isValidLocalPart(local)) found.eMail = t('employees.form.email.invalid');
    PHONE_FIELDS.forEach((name) => {
      if (formData[name] && !PHONE_PATTERN.test(formData[name])) found[name] = t('employees.form.phoneInvalid');
    });
    return found;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const found = validate();
    const { firstSection } = summarizeErrors(found);
    if (firstSection) {
      setErrors(found);
      setActiveSection(firstSection);
      return;
    }

    setSaving(true);
    try {
      // Check if email already exists (only if email is provided)
      if (formData.eMail && formData.eMail.trim() !== '') {
        const emailQuery = query(
          collection(db, 'Employees'),
          where('eMail', '==', formData.eMail.trim())
        );
        const emailSnapshot = await getDocs(emailQuery);

        if (!emailSnapshot.empty) {
          toast.error(t('employees.form.duplicateEmail'));
          setErrors({ eMail: t('employees.form.duplicateEmail') });
          setActiveSection('contact');
          return;
        }
      }

      // Convert boolean strings to actual boolean values
      const formattedData = {
        ...formData,
        isHandicappedPerson: formData.isHandicappedPerson === 'Yes',
        isIntern: formData.isIntern === 'Yes',
        isRetired: formData.isRetired === 'Yes',
      };

      await addDoc(collection(db, 'Employees'), formattedData);
      toast.success(t('employees.form.added'));
      onUpdate(); // Refresh the employee list
      onClose();
    } catch (error) {
      console.error('Error adding employee:', error);
      toast.error(t('employees.form.addError'));
    } finally {
      setSaving(false);
    }
  };

  const label = (name) => t(`employees.form.fields.${name}`);
  const textProps = (name) => ({ id: fieldId(name), name, value: formData[name], onChange: handleChange, error: errors[name] });
  const choose = t('employees.form.choose');

  const renderSection = () => {
    if (activeSection === 'contact') {
      return (
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <CompanyEmailField
            id={fieldId('eMail')}
            label={label('eMail')}
            value={formData.eMail}
            onChange={(value) => setField('eMail', value)}
            error={errors.eMail}
          />
          <Field label={label('phone')} htmlFor={fieldId('phone')} error={errors.phone}>
            <TextInput {...textProps('phone')} {...phoneHandlers('phone')} type="tel" inputMode="numeric" autoComplete="off" placeholder={t('employees.form.phonePlaceholder')} />
          </Field>
          <Field label={label('emergencyPhone')} htmlFor={fieldId('emergencyPhone')} error={errors.emergencyPhone}>
            <TextInput {...textProps('emergencyPhone')} {...phoneHandlers('emergencyPhone')} type="tel" inputMode="numeric" autoComplete="off" placeholder={t('employees.form.emergencyPlaceholder')} />
          </Field>
          <Field label={label('address')} htmlFor={fieldId('address')}>
            <TextArea {...textProps('address')} rows={3} />
          </Field>
          <Field label={label('workAddress')} htmlFor={fieldId('workAddress')}>
            <TextArea {...textProps('workAddress')} rows={3} />
          </Field>
        </div>
      );
    }

    if (activeSection === 'employment') {
      return (
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Field label={label('departman')} htmlFor={fieldId('departman')} hint={isLoadingDepartments ? t('employees.form.loadingDepartments') : undefined}>
            <SelectInput {...textProps('departman')} disabled={isLoadingDepartments} placeholder={choose} options={asOptions(departments)} />
          </Field>
          <Field label={label('profession')} htmlFor={fieldId('profession')}>
            <SelectInput {...textProps('profession')} placeholder={choose} options={asOptions(PROFESSION_OPTIONS)} />
          </Field>
          {/* Rol, eğitim/SOP/form atamalarındaki grup adlarıyla birebir
              eşleşmek zorunda olduğundan serbest metin yerine listeden seçilir */}
          <Field label={label('role')} htmlFor={fieldId('role')}>
            <SelectInput {...textProps('role')} placeholder={choose} options={asOptions(EMPLOYEE_ROLE_OPTIONS)} />
          </Field>
          <Field label={label('authorization')} htmlFor={fieldId('authorization')} hint={isLoadingAuthorizationRoles ? t('employees.form.loadingRoles') : undefined}>
            <SelectInput {...textProps('authorization')} disabled={isLoadingAuthorizationRoles} placeholder={choose} options={asOptions(authorizationRoles)} />
          </Field>
          <Field label={label('workType')} htmlFor={fieldId('workType')}>
            <SelectInput
              {...textProps('workType')}
              placeholder={choose}
              options={EMPLOYEE_COLUMNS.workType.options.map((option) => ({ value: option.value, label: optionLabel(t, option) }))}
            />
          </Field>
          <Field label={label('Active')}>
            <SegmentedControl
              options={[
                { value: '1', label: t('employees.status.active') },
                { value: '0', label: t('employees.status.inactive') },
              ]}
              value={String(formData.Active)}
              onChange={(value) => setField('Active', Number(value))}
              layoutId={`${idPrefix}-status`}
              label={label('Active')}
              size="sm"
            />
          </Field>
          <Field label={label('startDate')} htmlFor={fieldId('startDate')}>
            <TextInput {...textProps('startDate')} type="date" />
          </Field>
          <Field label={label('annualPermit')} htmlFor={fieldId('annualPermit')}>
            <TextInput {...textProps('annualPermit')} type="number" min="0" step="1" inputMode="numeric" placeholder="14" />
          </Field>
        </div>
      );
    }

    if (activeSection === 'education') {
      return (
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Field label={label('educationInfo')} htmlFor={fieldId('educationInfo')}>
            <SelectInput
              {...textProps('educationInfo')}
              placeholder={choose}
              options={EMPLOYEE_COLUMNS.educationInfo.options.map((option) => ({ value: option.value, label: optionLabel(t, option) }))}
            />
          </Field>
          <Field label={label('languageInfo')} htmlFor={fieldId('languageInfo')} className="sm:tw-col-span-2">
            <LanguageEditor
              idPrefix={idPrefix}
              entries={languageEntries}
              onChange={(next) => {
                setLanguageEntries(next);
                setDirty(true);
              }}
            />
          </Field>
        </div>
      );
    }

    return (
      <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
        <Field label={label('eName')} htmlFor={fieldId('eName')} required error={errors.eName}>
          <TextInput {...textProps('eName')} autoComplete="off" />
        </Field>
        <Field label={label('eSurname')} htmlFor={fieldId('eSurname')} required error={errors.eSurname}>
          <TextInput {...textProps('eSurname')} autoComplete="off" />
        </Field>
        <Field label={label('tcNumber')} htmlFor={fieldId('tcNumber')} required error={errors.tcNumber}>
          <TextInput {...textProps('tcNumber')} autoComplete="off" />
        </Field>
        <Field label={label('nationality')} htmlFor={fieldId('nationality')}>
          <SelectInput
            {...textProps('nationality')}
            options={NATIONALITY_OPTIONS.map((option) => ({ value: option.value, label: t(`employees.nationalities.${option.key}`) }))}
          />
        </Field>
        <Field label={label('birthday')} htmlFor={fieldId('birthday')}>
          <TextInput {...textProps('birthday')} type="date" />
        </Field>
        <div className="sm:tw-col-span-2">
          <p className="tw-mb-1.5 tw-px-0.5 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{t('employees.form.flags')}</p>
          <div className="tw-grid tw-gap-2 sm:tw-grid-cols-3">
            {FLAG_FIELDS.map((name) => (
              <ToggleTile
                key={name}
                id={fieldId(name)}
                label={label(name)}
                checked={formData[name] === 'Yes'}
                onChange={(on) => setField(name, on ? 'Yes' : 'No')}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const { counts } = summarizeErrors(errors);

  return (
    <EmployeeFormShell
      title={t('employees.form.addTitle')}
      subtitle={t('employees.form.addSubtitle')}
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="xs">
          <LuUserPlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      formId={`${idPrefix}-form`}
      sections={getFormSections(t)}
      activeSection={activeSection}
      onSectionChange={setActiveSection}
      sectionErrors={counts}
      hasErrors={Object.values(errors).some(Boolean)}
      onSubmit={handleSubmit}
      saving={saving}
      submitLabel={t('employees.form.saveNew')}
      dirty={dirty}
      onClose={onClose}
    >
      {renderSection()}
    </EmployeeFormShell>
  );
};

AddEmployeeModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default AddEmployeeModal;
